import { FormEvent, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dialog,
  Field,
  Fieldset,
  Heading,
  Input,
  Label,
  Paragraph,
  Radio,
  Select,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { AssessedArea, DataSourceError, DataSourceRequest } from '../../../api/workEnvironmentTypes';
import type { FactorDimension } from '../../../api/hms';
import { useAddDataSourceMutation } from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { FieldError, validationErrors } from '../assessmentView';
import {
  DATA_LEVELS,
  DATA_SOURCE_FIELDS,
  DATA_SOURCE_TYPES,
  dataSourceFieldErrors,
  newDataSource,
  today,
} from '../dataSourcesView';
import { areaName } from '../riskView';
import { FormErrorSummary, TextField } from './FormParts';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

type Props = { surveyId: number; area: AssessedArea; onClose: () => void };

const DIMENSIONS: FactorDimension[] = ['LOAD', 'RESOURCE'];

export const DataSourceDialog = ({ surveyId, area, onClose }: Props): ReactElement => {
  const { t } = useTranslation();
  const [form, setForm] = useState<DataSourceRequest>(() => newDataSource(area));
  const [errors, setErrors] = useState<FieldError[]>([]);
  const add = useAddDataSourceMutation(surveyId);
  const errorFor = (fieldId: string) => errors.find((error) => error.fieldId === fieldId)?.message;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    add.mutate(form, {
      onSuccess: onClose,
      onError: (error) => {
        const invalid = validationErrors<DataSourceError>(error);
        if (invalid) setErrors(dataSourceFieldErrors(invalid, t));
      },
    });
  };

  return (
    <Dialog open onClose={onClose} closedby="any" closeButton={t('workEnvironmentRisk.data.close')}>
      <form onSubmit={submit} noValidate className={classes.areaBody}>
        <div className={classes.savedText}>
          <Heading level={2} data-size="xs">
            {t('workEnvironmentRisk.data.dialogTitle')}
          </Heading>
          <Paragraph data-size="sm">{areaName(area, t)}</Paragraph>
        </div>
        <FormErrorSummary errors={errors} />
        <Field data-size="sm">
          <Label htmlFor={DATA_SOURCE_FIELDS.type}>{t('workEnvironmentRisk.data.type')}</Label>
          <Select
            id={DATA_SOURCE_FIELDS.type}
            value={form.type}
            onChange={(event) =>
              setForm({
                ...form,
                type: DATA_SOURCE_TYPES.find((type) => type === event.target.value) ?? form.type,
              })
            }
          >
            {DATA_SOURCE_TYPES.map((type) => (
              <Select.Option key={type} value={type}>
                {t(`workEnvironmentRisk.data.types.${type}`)}
              </Select.Option>
            ))}
          </Select>
        </Field>
        <Field data-size="sm">
          <Label htmlFor={DATA_SOURCE_FIELDS.title}>{t('workEnvironmentRisk.data.title')}</Label>
          <Input
            id={DATA_SOURCE_FIELDS.title}
            value={form.title}
            aria-invalid={Boolean(errorFor(DATA_SOURCE_FIELDS.title)) || undefined}
            onChange={(event) => setForm({ ...form, title: event.target.value })}
          />
          {errorFor(DATA_SOURCE_FIELDS.title) && (
            <ValidationMessage>{errorFor(DATA_SOURCE_FIELDS.title)}</ValidationMessage>
          )}
        </Field>
        <Field data-size="sm">
          <Label htmlFor={DATA_SOURCE_FIELDS.date}>{t('workEnvironmentRisk.data.date')}</Label>
          <Input
            id={DATA_SOURCE_FIELDS.date}
            type="date"
            max={today()}
            value={form.date}
            aria-invalid={Boolean(errorFor(DATA_SOURCE_FIELDS.date)) || undefined}
            onChange={(event) => setForm({ ...form, date: event.target.value })}
          />
          {errorFor(DATA_SOURCE_FIELDS.date) && (
            <ValidationMessage>{errorFor(DATA_SOURCE_FIELDS.date)}</ValidationMessage>
          )}
        </Field>
        <TextField
          id={DATA_SOURCE_FIELDS.summary}
          label={t('workEnvironmentRisk.data.summary')}
          help={t('workEnvironmentRisk.data.summaryHelp')}
          value={form.summary}
          error={errorFor(DATA_SOURCE_FIELDS.summary)}
          onChange={(summary) => setForm({ ...form, summary })}
        />
        <Fieldset data-size="sm">
          <Fieldset.Legend>{t('workEnvironmentRisk.data.dimension')}</Fieldset.Legend>
          {DIMENSIONS.map((dimension) => (
            <Radio
              key={dimension}
              id={`data-source-dimension-${dimension}`}
              name="data-source-dimension"
              value={dimension}
              label={t(`workEnvironmentRisk.data.dimensions.${dimension}`)}
              checked={form.dimension === dimension}
              onChange={() => setForm({ ...form, dimension })}
            />
          ))}
        </Fieldset>
        <Fieldset data-size="sm">
          <Fieldset.Legend>{t(`workEnvironmentRisk.data.levelQuestion.${form.dimension}`)}</Fieldset.Legend>
          {DATA_LEVELS.map((level) => (
            <Radio
              key={level}
              id={`data-source-level-${level}`}
              name="data-source-level"
              value={String(level)}
              label={`${level} – ${t(`workEnvironmentRisk.data.levels.${form.dimension}.${level}`)}`}
              checked={form.level === level}
              onChange={() => setForm({ ...form, level })}
            />
          ))}
        </Fieldset>
        {add.isError && !validationErrors(add.error) && (
          <Alert data-color="danger">
            <Paragraph>{t('workEnvironmentRisk.data.saveFailed')}</Paragraph>
          </Alert>
        )}
        <div className={classes.dialogButtons}>
          <Button type="submit" variant="primary" data-size="sm" loading={add.isPending}>
            {t('workEnvironmentRisk.data.save')}
          </Button>
          <Button type="button" variant="tertiary" data-size="sm" onClick={onClose}>
            {t('workEnvironmentRisk.assessment.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
